import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DotLoading, Empty, Tag, Toast } from 'antd-mobile';
import { LeftOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  checklistItemLabel,
  countFailedItems,
  downloadInspectionPdf,
  expandAfterChecklistItems,
  expandBeforeChecklistItems,
  FITNESS_QUESTION_IDS,
  formatDateTime,
  isFitnessPassing,
  parseFitnessAnswers,
  supabase,
  type ChecklistViewRow,
  type VehicleInspection,
} from '@ubus/shared';

type DetailRow = VehicleInspection & {
  vehicles?: {
    fleet_number?: string | null;
    registration_plate?: string | null;
    model?: string | null;
  } | null;
};

function vehicleLabel(row: DetailRow): string {
  const v = row.vehicles;
  if (v) {
    const plate = v.registration_plate?.trim();
    const fleet = v.fleet_number?.trim();
    if (plate && fleet) return `${plate} (${fleet})`;
    if (plate || fleet) return plate || fleet || '—';
    if (v.model?.trim()) return v.model.trim();
  }
  return row.work_fields?.rego?.trim() || '—';
}

function afterStatusColor(status: ChecklistViewRow['status']): 'success' | 'danger' | 'warning' | 'default' {
  if (status === 'pass') return 'success';
  if (status === 'fail') return 'danger';
  if (status === 'unanswered') return 'warning';
  return 'default';
}

function InspectionHistoryDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<DetailRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showAfterFull, setShowAfterFull] = useState(false);
  const [showBeforeFull, setShowBeforeFull] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setRow(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vehicle_inspections')
        .select('*, vehicles(fleet_number, registration_plate, model)')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      setRow((data as DetailRow | null) ?? null);
      setShowAfterFull(false);
      setShowBeforeFull(false);
    } catch (err) {
      console.error('[InspectionHistoryDetail]', err);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDownload = async () => {
    if (!row) return;
    setDownloading(true);
    try {
      await downloadInspectionPdf(row);
      Toast.show({ icon: 'success', content: t('inspectionHistory.pdfReady') });
    } catch (err) {
      console.error('[InspectionHistoryDetail] pdf', err);
      Toast.show({ icon: 'fail', content: t('inspectionHistory.pdfFailed') });
    } finally {
      setDownloading(false);
    }
  };

  const fitness = row ? parseFitnessAnswers(row.work_fields?.fitness) : null;
  const fails = (row?.items ?? []).filter((i) => i.status === 'fail');
  const afterFails = (row?.after_items ?? []).filter((i) => i.status === 'fail');
  const beforeRows = useMemo(() => expandBeforeChecklistItems(row?.items), [row]);
  const afterRows = useMemo(() => expandAfterChecklistItems(row?.after_items), [row]);
  const afterStarted =
    (row?.after_items?.length ?? 0) > 0 || !!row?.after_completed_at;

  return (
    <div className="driver-detail">
      <header className="driver-detail-nav">
        <button
          type="button"
          className="driver-detail-nav-back"
          onClick={() => navigate('/inspection-history')}
          aria-label={t('common.back')}
        >
          <LeftOutline fontSize={22} />
        </button>
        <h1 className="driver-detail-nav-title">{t('inspectionHistory.detailTitle')}</h1>
        <span className="driver-detail-nav-spacer" aria-hidden="true" />
      </header>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <DotLoading color="primary" />
        </div>
      ) : !row ? (
        <Empty description={t('inspectionHistory.notFound')} />
      ) : (
        <div className="driver-section" style={{ padding: '12px 16px 32px' }}>
          <div className="driver-card" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('inspectionHistory.summary')}</div>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div>
                {t('inspectionHistory.date')}: {row.inspection_date}
              </div>
              <div>
                {t('inspectionHistory.vehicle')}: {vehicleLabel(row)}
              </div>
              <div>
                {t('inspectionHistory.severityLabel')}:{' '}
                {row.severity_result
                  ? t(`inspectionHistory.severity.${row.severity_result}`, {
                      defaultValue: row.severity_result,
                    })
                  : '—'}
              </div>
              <div>
                {t('inspectionHistory.declaredAt')}:{' '}
                {row.declaration_accepted_at
                  ? formatDateTime(row.declaration_accepted_at)
                  : '—'}
              </div>
              <div>
                {t('inspectionHistory.fails')}: {countFailedItems(row.items)}
              </div>
              <div>
                {t('inspectionHistory.fitness')}:{' '}
                {isFitnessPassing(row.work_fields?.fitness)
                  ? t('inspectionHistory.fitnessPass')
                  : t('inspectionHistory.fitnessFail')}
              </div>
              <div>
                {t('inspectionHistory.afterDone')}:{' '}
                {row.after_completed_at
                  ? t('inspectionHistory.afterDoneYes')
                  : t('inspectionHistory.afterDoneNo')}
              </div>
              <div>
                {t('inspectionHistory.afterCompletedAt')}:{' '}
                {row.after_completed_at ? formatDateTime(row.after_completed_at) : '—'}
              </div>
            </div>
          </div>

          <div className="driver-card" style={{ padding: 14, marginBottom: 12 }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>{t('inspectionHistory.beforeChecklist')}</span>
              <Button
                size="mini"
                fill="none"
                onClick={() => setShowBeforeFull((v) => !v)}
              >
                {showBeforeFull
                  ? t('inspectionHistory.hideFullBefore')
                  : t('inspectionHistory.viewFullBefore')}
              </Button>
            </div>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
              {t('inspectionHistory.failedItems')}
            </div>
            {fails.length === 0 ? (
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                {t('inspectionHistory.noFails')}
              </div>
            ) : (
              fails.map((item) => (
                <div key={item.id} style={{ fontSize: 13, marginBottom: 6 }}>
                  {checklistItemLabel(item.id)}
                  {item.note ? ` — ${item.note}` : ''}
                </div>
              ))
            )}
            {showBeforeFull ? (
              <>
                <div style={{ fontWeight: 600, margin: '12px 0 8px', fontSize: 13 }}>
                  {t('inspectionHistory.beforeFullList')}
                </div>
                {beforeRows.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      fontSize: 13,
                      marginBottom: 8,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>{checklistItemLabel(item.id)}</span>
                      <Tag color={afterStatusColor(item.status)} fill="outline">
                        {t(`inspectionHistory.itemStatus.${item.status}`)}
                      </Tag>
                    </div>
                    {item.note ? (
                      <div style={{ color: '#64748b', paddingLeft: 2 }}>{item.note}</div>
                    ) : null}
                  </div>
                ))}
              </>
            ) : null}
          </div>

          {fitness && (
            <div className="driver-card" style={{ padding: 14, marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {t('inspectionHistory.fitnessAnswers')}
              </div>
              {FITNESS_QUESTION_IDS.map((qid) => {
                const v = fitness.answers[qid];
                return (
                  <div key={qid} style={{ fontSize: 13, marginBottom: 6 }}>
                    {t(`inspection.fitnessQuestions.${qid}`)}:{' '}
                    {v === true ? t('common.yes') : v === false ? t('common.no') : '—'}
                  </div>
                );
              })}
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                {fitness.signed_name || '—'} ·{' '}
                {fitness.signed_at ? formatDateTime(fitness.signed_at) : '—'}
              </div>
            </div>
          )}

          <div className="driver-card" style={{ padding: 14, marginBottom: 12 }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>{t('inspectionHistory.afterChecklist')}</span>
              {afterStarted ? (
                <Button
                  size="mini"
                  fill="none"
                  onClick={() => setShowAfterFull((v) => !v)}
                >
                  {showAfterFull
                    ? t('inspectionHistory.hideFullAfter')
                    : t('inspectionHistory.viewFullAfter')}
                </Button>
              ) : null}
            </div>
            {!afterStarted ? (
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {t('inspectionHistory.afterNotStarted')}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
                  <div>
                    {t('inspectionHistory.finishKm')}: {row.work_fields?.finish_km || '—'}
                  </div>
                  <div>
                    {t('inspectionHistory.depotFinish')}:{' '}
                    {row.work_fields?.depot_finish_time || '—'}
                  </div>
                </div>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                  {t('inspectionHistory.afterFails')}
                </div>
                {afterFails.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                    {t('inspectionHistory.noAfterFails')}
                  </div>
                ) : (
                  afterFails.map((item) => (
                    <div key={item.id} style={{ fontSize: 13, marginBottom: 6 }}>
                      {checklistItemLabel(item.id)}
                      {item.note ? ` — ${item.note}` : ''}
                    </div>
                  ))
                )}
                {showAfterFull ? (
                  <>
                    <div style={{ fontWeight: 600, margin: '12px 0 8px', fontSize: 13 }}>
                      {t('inspectionHistory.afterFullList')}
                    </div>
                    {afterRows.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          fontSize: 13,
                          marginBottom: 8,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span>{checklistItemLabel(item.id)}</span>
                          <Tag color={afterStatusColor(item.status)} fill="outline">
                            {t(`inspectionHistory.itemStatus.${item.status}`)}
                          </Tag>
                        </div>
                        {item.note ? (
                          <div style={{ color: '#64748b', paddingLeft: 2 }}>{item.note}</div>
                        ) : null}
                      </div>
                    ))}
                  </>
                ) : null}
              </>
            )}
          </div>

          <Button
            color="primary"
            block
            loading={downloading}
            onClick={() => {
              void handleDownload();
            }}
          >
            {t('inspectionHistory.downloadPdf')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default InspectionHistoryDetail;
